import type { TaxonomyIndex, TaxonomyNode, TaxonomyTreeRow } from "./types";

export const indexTree = <TId extends string>(roots: readonly TaxonomyNode<TId>[]): TaxonomyIndex<TId> => {
    const byId = new Map<TId, TaxonomyNode<TId>>();
    const parentById = new Map<TId, TId>();
    const countById = new Map<TId, number>();

    // Pre-order insertion, so `byId.keys()` doubles as a stable document order.
    const visit = (node: TaxonomyNode<TId>, parent?: TaxonomyNode<TId>): number => {
        byId.set(node.id, node);

        if (parent) {
            parentById.set(node.id, parent.id);
        }

        let descendantLeaves = 0;

        for (const child of node.children) {
            descendantLeaves += visit(child, node);
        }

        const isLeaf = node.children.length === 0;
        const leafCount = isLeaf ? 1 : descendantLeaves;

        // A leaf's leaf-descendant count is itself, so the 1 it would show says nothing about the row.
        // Only a branch's count tells the user how much sits below it. The leaf still returns its 1, so
        // ancestors keep rolling up correctly.
        if (!isLeaf) {
            countById.set(node.id, leafCount);
        }

        return leafCount;
    };

    for (const root of roots) {
        visit(root);
    }

    return { roots, byId, parentById, countById };
};

const ancestorIdsOf = <TId extends string>(index: TaxonomyIndex<TId>, id: TId): TId[] => {
    const ancestors: TId[] = [];

    for (let cursor = index.parentById.get(id); cursor; cursor = index.parentById.get(cursor)) {
        ancestors.push(cursor);
    }

    return ancestors;
};

const findCoveringAncestor = <TId extends string>(
    selected: ReadonlySet<TId>,
    index: TaxonomyIndex<TId>,
    id: TId,
): TId | undefined => ancestorIdsOf(index, id).find((ancestor) => selected.has(ancestor));

const descendantIdsOf = <TId extends string>(index: TaxonomyIndex<TId>, id: TId): TId[] => {
    const node = index.byId.get(id);

    if (!node) {
        return [];
    }

    const descendants: TId[] = [];
    const stack = [...node.children];

    while (stack.length > 0) {
        const current = stack.pop() as TaxonomyNode<TId>;

        descendants.push(current.id);
        stack.push(...current.children);
    }

    return descendants;
};

/** Path of ids from `ancestor` down to `id`, inclusive at both ends. */
const pathBetween = <TId extends string>(index: TaxonomyIndex<TId>, ancestor: TId, id: TId): readonly TId[] => {
    const upwards = [id, ...ancestorIdsOf(index, id)];
    const cut = upwards.indexOf(ancestor);

    return upwards.slice(0, cut + 1).reverse();
};

/**
 * Collapses any node whose children are *all* selected into that node, bottom-up to a fixpoint.
 * "Covered" strictly means `child ∈ selected` — which coincides with "checked" only because the
 * input is already canonical below the nodes being collapsed.
 */
const collapseFullParents = <TId extends string>(selected: Set<TId>, index: TaxonomyIndex<TId>): void => {
    let changed = true;

    while (changed) {
        changed = false;

        const candidates = new Set(
            [...selected].map((id) => index.parentById.get(id)).filter((id): id is TId => id !== undefined),
        );

        for (const parentId of candidates) {
            const parent = index.byId.get(parentId);

            // A leaf is vacuously "fully covered"; never collapse one into itself.
            if (!parent || parent.children.length === 0) {
                continue;
            }

            if (parent.children.every((child) => selected.has(child.id))) {
                parent.children.forEach((child) => selected.delete(child.id));
                selected.add(parentId);
                changed = true;
            }
        }
    }
};

const inDocumentOrder = <TId extends string>(selected: ReadonlySet<TId>, index: TaxonomyIndex<TId>): TId[] =>
    [...index.byId.keys()].filter((id) => selected.has(id));

/**
 * Establishes the canonical invariant over the ids the index resolves, and passes ids it does not
 * resolve through untouched. Unknown ids mean either "taxonomy not loaded yet" or "node deleted
 * upstream"; both must survive a round trip, or restoring a saved search before the taxonomy
 * resolves would silently empty it — and persist that emptiness on the next save.
 */
export const canonicaliseValues = <TId extends string>(
    values: readonly TId[],
    index: TaxonomyIndex<TId>,
): readonly TId[] => {
    const unknown = values.filter((id) => !index.byId.has(id));
    const selected = new Set(values.filter((id) => index.byId.has(id)));

    for (const id of [...selected]) {
        if (findCoveringAncestor(selected, index, id)) {
            selected.delete(id);
        }
    }

    collapseFullParents(selected, index);

    return [...inDocumentOrder(selected, index), ...unknown];
};

/**
 * Toggling a node checked via an ancestor means "that ancestor, except this node": the covering
 * ancestor is replaced by the siblings along the path down to the node. Cost is `(b-1)` ids per
 * level of the path, never the whole subtree.
 */
export const toggleNode = <TId extends string>(
    values: readonly TId[],
    id: TId,
    index: TaxonomyIndex<TId>,
): readonly TId[] => {
    if (!index.byId.has(id)) {
        return values;
    }

    const unknown = values.filter((nodeId) => !index.byId.has(nodeId));
    const selected = new Set(values.filter((nodeId) => index.byId.has(nodeId)));

    if (selected.has(id)) {
        selected.delete(id);
    } else {
        const coveringAncestor = findCoveringAncestor(selected, index, id);

        if (coveringAncestor) {
            selected.delete(coveringAncestor);

            const path = pathBetween(index, coveringAncestor, id);

            path.slice(0, -1).forEach((parentId, depth) => {
                const parent = index.byId.get(parentId) as TaxonomyNode<TId>;
                const nextOnPath = path[depth + 1];

                parent.children.forEach((child) => {
                    if (child.id !== nextOnPath) {
                        selected.add(child.id);
                    }
                });
            });
        } else {
            selected.add(id);
            descendantIdsOf(index, id).forEach((descendant) => selected.delete(descendant));
        }
    }

    collapseFullParents(selected, index);

    return [...inDocumentOrder(selected, index), ...unknown];
};

/** Strict ancestors of every selected node — the nodes that render `indeterminate`. */
export const collectAncestorsOfChecked = <TId extends string>(
    values: readonly TId[],
    index: TaxonomyIndex<TId>,
): ReadonlySet<TId> => {
    const ancestors = new Set<TId>();

    values.forEach((id) => ancestorIdsOf(index, id).forEach((ancestor) => ancestors.add(ancestor)));

    return ancestors;
};

/**
 * The leaves a selection actually matches. Used to diff two selections: comparing raw ids would
 * report a *widening* change as deletions, because checking a parent collapses its children away.
 * Unknown ids stand for themselves so a change involving them is still observable.
 */
export const impliedLeafIds = <TId extends string>(
    values: readonly TId[],
    index: TaxonomyIndex<TId>,
): ReadonlySet<TId> => {
    const leaves = new Set<TId>();

    values.forEach((id) => {
        const node = index.byId.get(id);

        if (!node || node.children.length === 0) {
            leaves.add(id);
            return;
        }

        descendantIdsOf(index, id).forEach((descendantId) => {
            if (index.byId.get(descendantId)?.children.length === 0) {
                leaves.add(descendantId);
            }
        });
    });

    return leaves;
};

/** Matched nodes plus the ancestor chain needed to render them — never the matched nodes' subtrees. */
export const revealAncestors = <TId extends string>(
    matched: ReadonlySet<TId>,
    index: TaxonomyIndex<TId>,
): ReadonlySet<TId> => {
    const visible = new Set<TId>(matched);

    matched.forEach((id) => ancestorIdsOf(index, id).forEach((ancestor) => visible.add(ancestor)));

    return visible;
};

type FlattenOptions<TId extends string> = {
    readonly expanded: ReadonlySet<TId>;
    readonly checked: ReadonlySet<TId>;
    readonly ancestorsOfChecked: ReadonlySet<TId>;
    /** Search results widened by `revealAncestors`. Omit when no search is active. */
    readonly visible?: ReadonlySet<TId>;
    /**
     * Whether checking a node also renders its descendants checked. True for a canonical multi-select
     * tree, where a checked parent stands for its whole subtree. A single-select tree must set this
     * false: only one row may read as selected, however many the selection matches downstream.
     */
    readonly checkedCoversDescendants?: boolean;
};

/**
 * Flattens the visible tree into windowable rows in one downward pass, resolving each row's check
 * state in O(1) by inheriting the parent's "checked via an ancestor" flag.
 */
export const flattenVisibleRows = <TId extends string>(
    index: TaxonomyIndex<TId>,
    { expanded, checked, ancestorsOfChecked, visible, checkedCoversDescendants = true }: FlattenOptions<TId>,
): readonly TaxonomyTreeRow<TId>[] => {
    const rows: TaxonomyTreeRow<TId>[] = [];

    const visit = (node: TaxonomyNode<TId>, depth: number, checkedByAncestor: boolean): void => {
        if (visible && !visible.has(node.id)) {
            return;
        }

        const isChecked = checkedByAncestor || checked.has(node.id);
        const hasChildren = node.children.length > 0;
        // A search reveals its own matches; it must not write into the user's manual expansion set.
        const isExpanded =
            hasChildren && (visible ? node.children.some((child) => visible.has(child.id)) : expanded.has(node.id));

        rows.push({
            node,
            depth,
            hasChildren,
            isExpanded,
            checkState: isChecked ? "checked" : ancestorsOfChecked.has(node.id) ? "indeterminate" : "unchecked",
            count: index.countById.get(node.id),
        });

        if (isExpanded) {
            node.children.forEach((child) => visit(child, depth + 1, checkedCoversDescendants && isChecked));
        }
    };

    index.roots.forEach((root) => visit(root, 0, false));

    return rows;
};
